/**
 * Shared reverse-geocode helper for fleet tables.
 * Uses OpenStreetMap Nominatim (already used on the vehicle track page)
 * with memory + localStorage caching so repeated GPS polls do not re-hit the API.
 */

const CACHE_STORAGE_KEY = "routeeye.reverseGeocode.v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
// ~11m grid — small GPS jitter keeps the same place name.
const COORD_PRECISION = 4;
const NOMINATIM_MIN_INTERVAL_MS = 1100;

const memoryCache = new Map();
const inFlight = new Map();
let queue = Promise.resolve();
let lastRequestAt = 0;

const roundCoord = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Number(num.toFixed(COORD_PRECISION));
};

export const buildGeocodeCacheKey = (latitude, longitude) => {
  const lat = roundCoord(latitude);
  const lng = roundCoord(longitude);
  if (lat === null || lng === null) return null;
  if (lat === 0 && lng === 0) return null;
  return `${lat},${lng}`;
};

const readPersistedCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writePersistedCache = (entries) => {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Ignore quota / private-mode failures; memory cache still works.
  }
};

const getCachedAddress = (cacheKey) => {
  const memoryHit = memoryCache.get(cacheKey);
  if (memoryHit?.address) return memoryHit.address;

  const persisted = readPersistedCache()[cacheKey];
  if (!persisted?.address || !persisted?.savedAt) return null;
  if (Date.now() - persisted.savedAt > CACHE_TTL_MS) return null;

  memoryCache.set(cacheKey, persisted);
  return persisted.address;
};

const setCachedAddress = (cacheKey, address) => {
  const entry = { address, savedAt: Date.now() };
  memoryCache.set(cacheKey, entry);
  const persisted = readPersistedCache();
  persisted[cacheKey] = entry;

  const keys = Object.keys(persisted);
  if (keys.length > 500) {
    keys
      .sort((a, b) => (persisted[a].savedAt || 0) - (persisted[b].savedAt || 0))
      .slice(0, keys.length - 500)
      .forEach((key) => delete persisted[key]);
  }

  writePersistedCache(persisted);
};

const formatNominatimAddress = (data, latitude, longitude) => {
  if (data?.address) {
    const parts = [];
    if (data.address.road) parts.push(data.address.road);
    if (data.address.neighbourhood) parts.push(data.address.neighbourhood);
    else if (data.address.suburb) parts.push(data.address.suburb);
    if (data.address.city) parts.push(data.address.city);
    else if (data.address.town) parts.push(data.address.town);
    else if (data.address.village) parts.push(data.address.village);
    if (data.address.state) parts.push(data.address.state);
    if (parts.length) return parts.join(", ");
  }

  if (data?.display_name) {
    return data.display_name.split(",").slice(0, 4).join(",").trim();
  }

  return `${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)}`;
};

const waitForNominatimSlot = async () => {
  const waitMs = Math.max(0, NOMINATIM_MIN_INTERVAL_MS - (Date.now() - lastRequestAt));
  if (waitMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, waitMs));
  }
  lastRequestAt = Date.now();
};

const fetchNominatimAddress = async (latitude, longitude) => {
  await waitForNominatimSlot();

  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
    {
      headers: {
        "User-Agent": "RouteEyeVehicleTracking/1.0 (Vehicle Tracking Application)",
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Nominatim reverse geocode failed (${response.status})`);
  }

  const data = await response.json();
  return formatNominatimAddress(data, latitude, longitude);
};

/**
 * Resolve a human-readable place name for coordinates.
 * Returns cached values immediately when available.
 */
export const reverseGeocode = async (latitude, longitude) => {
  const cacheKey = buildGeocodeCacheKey(latitude, longitude);
  if (!cacheKey) return null;

  const cached = getCachedAddress(cacheKey);
  if (cached) return cached;

  if (inFlight.has(cacheKey)) {
    return inFlight.get(cacheKey);
  }

  const request = queue
    .catch(() => undefined)
    .then(async () => {
      const again = getCachedAddress(cacheKey);
      if (again) return again;

      try {
        const address = await fetchNominatimAddress(latitude, longitude);
        setCachedAddress(cacheKey, address);
        return address;
      } catch (error) {
        console.warn("Reverse geocode failed:", error);
        return null;
      } finally {
        inFlight.delete(cacheKey);
      }
    });

  inFlight.set(cacheKey, request);
  queue = request.then(
    () => undefined,
    () => undefined,
  );
  return request;
};

/**
 * Resolve many vehicle positions. Uses cache first, then rate-limited lookups.
 * onResolved(vehicleId, address) is called as each name becomes available.
 */
export const resolveVehicleLocationNames = async (
  vehicles = [],
  { getCoordinates, onResolved } = {},
) => {
  if (typeof getCoordinates !== "function") return {};

  const resolved = {};
  const pending = [];

  vehicles.forEach((vehicle) => {
    const coords = getCoordinates(vehicle);
    const latitude = Number(coords?.latitude);
    const longitude = Number(coords?.longitude);
    const cacheKey = buildGeocodeCacheKey(latitude, longitude);
    if (!cacheKey) return;

    const vehicleId = String(vehicle.id);
    const cached = getCachedAddress(cacheKey);
    if (cached) {
      resolved[vehicleId] = cached;
      onResolved?.(vehicleId, cached);
      return;
    }

    pending.push({ vehicleId, latitude, longitude, cacheKey });
  });

  for (const item of pending) {
    const address = await reverseGeocode(item.latitude, item.longitude);
    if (!address) continue;
    resolved[item.vehicleId] = address;
    onResolved?.(item.vehicleId, address);
  }

  return resolved;
};
